import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Search } from "lucide-react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "../../Styles/yellowcalfcss/ExpenseType.css";
import ConfirmModal from "../../components/ConfirmModal";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";

export function ExpenseType() {
  /* ---------------- STATE ---------------- */
  const customer_currency = (localStorage.getItem("customer_currency") || "").replace(/"/g, "");
  const [recordsList, setRecordsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nameSearch, setNameSearch] = useState("");
  const [priceSearch, setPriceSearch] = useState("");

  const [displayModal, setDisplayModal] = useState(false);
  const [currentEditingRecord, setCurrentEditingRecord] = useState(null);

  const [formName, setFormName] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------- FETCH LIST ---------------- */
  const fetchExpenseTypes = async () => {
    setLoading(true);

    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      name_search: nameSearch,
      price_search: priceSearch,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.expenseTypeList,
        JSON.stringify(requestBody)
      );

      if (res?.result) {
        setRecordsList(res.data || []);
      } else {
        setRecordsList([]);
        toast.error(res?.message || "Failed to load data");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenseTypes();
  }, []);

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const errors = {};
    if (!formName.trim()) errors.name = "Name is required";
    if (!formPrice || Number(formPrice) <= 0)
      errors.price = "Valid price required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ---------------- CREATE / UPDATE ---------------- */
  const handleSaveRecord = async () => {
    if (!validateForm()) return;

    setSaving(true);

    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      name: formName,
      price: Number(formPrice),
      ...(currentEditingRecord && { id: currentEditingRecord.expense_type_id }),
    };

    const api = currentEditingRecord
      ? ApiConfig.expenseTypeUpdate
      : ApiConfig.expenseTypeCreate;

    try {
      const res = await simplePostCall(api, JSON.stringify(requestBody));
      if (res?.result) {
        toast.success(res.message);
        setDisplayModal(false);
        fetchExpenseTypes();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDeleteIncome = async () => {
    setIsDeleting(true);

    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: deleteId,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.expenseTypeDelete,
        JSON.stringify(requestBody)
      );

      if (res?.result) {
        toast.success(res.message);
        fetchExpenseTypes();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
  const timer = setTimeout(() => {
    fetchExpenseTypes();
  }, 500); // ⏳ 500ms debounce

  return () => clearTimeout(timer);
}, [nameSearch, priceSearch]);

  /* ---------------- UI ---------------- */
  return (
    <div className="income-type-container">
       <ToastContainer position="top-right" autoClose={3000} />
      {/* HEADER */}
      <div className="special-header panel-white-all">
        <h4 className="page-title">Expense Type</h4>
        <div className="action-buttons-group">
          <Button
            className="btn btn-primary"
            onClick={() => {
              setCurrentEditingRecord(null);
              setFormName("");
              setFormPrice("");
              setFormErrors({});
              setDisplayModal(true);
            }}
          >
            Create Expense Type
          </Button>
        </div>
      </div>

      <div className="yc-list-body">
        {/* FILTERS */}
        <div className="yc-list-filter">
          <Form.Control
            placeholder="Search Name"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            className="exp-type-search-name"
          />
          <Form.Control
            placeholder="Search Price"
            value={priceSearch}
            onChange={(e) => setPriceSearch(e.target.value)}
            className="exp-type-search-price"
          />
        </div>

        {/* TABLE HEADER */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-4">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Name</div>
            <div className="yc-list-cell">Price</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {/* TABLE BODY */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "20vh" }}>
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : recordsList.length === 0 ? (
          <div className="yc-empty-state">No data found</div>
        ) : (
          <div className="yc-list-row-list">
            {recordsList.map((record, index) => (
              <div key={record.expense_type_id} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-4">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell">{record.expense_type_name}</div>
                  <div className="yc-list-cell">
                    {customer_currency} {formatNumber(record.expense_type_price)}
                  </div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon"
                      title="Edit"
                      onClick={() => {
                        setCurrentEditingRecord(record);
                        setFormName(record.expense_type_name);
                        setFormPrice(record.expense_type_price);
                        setFormErrors({});
                        setDisplayModal(true);
                      }}
                    >
                      <FaEdit size={16} />
                    </button>

                    <button
                      className="yc-action-icon yc-action-icon--danger"
                      title="Delete"
                      onClick={() => {
                        setDeleteId(record.expense_type_id);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      <Modal show={displayModal} onHide={() => setDisplayModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentEditingRecord ? "Edit Expense Type" : "Create Expense Type"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={formName}
                isInvalid={!!formErrors.name}
                onChange={(e) => setFormName(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Price</Form.Label>
              <Form.Control
                type="number"
                value={formPrice}
                isInvalid={!!formErrors.price}
                onChange={(e) => setFormPrice(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.price}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDisplayModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveRecord} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRM */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete Income"
        message="Are you sure you want to delete this income record?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteIncome}
        isLoading={isDeleting}
      />
    </div>
  );
}
